// Mock data for Futura Softwares BV website

export const caseStudies = [
  {
    id: '1',
    slug: 'automotive-ai-optimization',
    title: 'Automotive Supply Chain AI Optimization',
    clientType: 'Enterprise',
    sector: 'Automotive',
    serviceType: 'AI Tools',
    challenge: 'A leading automotive manufacturer struggled with inefficient supply chain management, resulting in delays and excess inventory costs exceeding €2M annually.',
    approach: 'Designed and deployed autonomous AI agents to monitor real-time supply data, predict demand fluctuations, and automatically adjust procurement workflows.',
    solution: 'Built a multi-agent system integrated with ERP and logistics APIs, featuring predictive analytics dashboards and automated decision-making workflows.',
    results: [
      '42% reduction in inventory holding costs',
      '28% improvement in delivery time accuracy',
      'Real-time visibility across 150+ suppliers'
    ],
    metrics: { costReduction: '42%', timeImprovement: '28%', supplierVisibility: '150+' },
    quote: 'The AI system transformed our supply chain from reactive to predictive. We now anticipate issues before they become problems.',
    image: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800'
  },
  {
    id: '2',
    slug: 'government-hfc-reduction-strategy',
    title: 'National HFC Phase-Down Strategy for EU Member State',
    clientType: 'Government',
    sector: 'Public Policy',
    serviceType: 'HFC & CO₂',
    challenge: 'A European government needed to comply with Kigali Amendment targets while minimizing economic disruption to local HVAC and refrigeration industries.',
    approach: 'AI-powered scenario modeling to evaluate 50+ policy pathways, stakeholder impact analysis, and roadmap development aligned with EU regulations.',
    solution: 'Delivered a comprehensive 10-year transition strategy with sector-specific timelines, incentive programs, and AI-driven monitoring dashboards.',
    results: [
      '65% projected HFC reduction by 2030',
      '€120M in estimated climate compliance savings',
      'Framework adopted across 3 adjacent regions'
    ],
    metrics: { hfcReduction: '65%', costSavings: '€120M', adoption: '3 regions' },
    quote: 'The AI modeling gave us confidence in our policy decisions and helped us communicate clearly with industry stakeholders.',
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800'
  },
  {
    id: '3',
    slug: 'energy-decarbonization-dashboard',
    title: 'Energy Company Decarbonization Dashboard',
    clientType: 'Corporate',
    sector: 'Energy',
    serviceType: 'Both',
    challenge: 'A multinational energy company lacked visibility into its CO₂ emissions across 40+ facilities and needed executive-ready reporting for board meetings.',
    approach: 'Built AI agents to aggregate emissions data from disparate systems and a real-time dashboard with scenario planning capabilities.',
    solution: 'Integrated platform combining data extraction agents, emissions modeling, and executive dashboards with Paris Agreement alignment tracking.',
    results: [
      'Unified view of 12M+ tons CO₂ emissions',
      '34% faster reporting cycle time',
      'Identified €8M in abatement opportunities'
    ],
    metrics: { emissionsTracked: '12M+ tons', efficiency: '34%', opportunities: '€8M' },
    quote: 'We finally have a single source of truth for our climate commitments. The AI insights revealed hidden opportunities we never knew existed.',
    image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=800'
  }
];

export const blogPosts = [
  {
    id: '1',
    slug: 'ai-agents-sustainability',
    title: 'How AI Agents Are Accelerating Corporate Sustainability',
    category: 'AI',
    summary: 'Exploring the intersection of autonomous AI systems and climate action, and why agentic AI is the key to meeting 2030 emission targets.',
    content: 'Full article content here...',
    publishDate: '2025-01-15',
    author: 'Dr. Lena van Berg',
    heroImage: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800',
    readTime: '8 min read'
  },
  {
    id: '2',
    slug: 'kigali-amendment-2025',
    title: 'Kigali Amendment 2025: What Companies Need to Know',
    category: 'Sustainability',
    summary: 'Breaking down the latest HFC phase-down requirements and practical steps for businesses to achieve compliance.',
    content: 'Full article content here...',
    publishDate: '2025-01-08',
    author: 'Marcus Osei',
    heroImage: 'https://images.unsplash.com/photo-1497435334941-8c899ee9e8e9?w=800',
    readTime: '6 min read'
  },
  {
    id: '3',
    slug: 'llm-enterprise-tools',
    title: 'Building Production-Grade AI Tools with LLMs',
    category: 'AI',
    summary: 'Technical deep-dive into architecting reliable, scalable AI agent systems for enterprise use cases.',
    content: 'Full article content here...',
    publishDate: '2024-12-20',
    author: 'Dr. Lena van Berg',
    heroImage: 'https://images.unsplash.com/photo-1555255707-c07966088b7b?w=800',
    readTime: '12 min read'
  },
  {
    id: '4',
    slug: 'scope-3-emissions-tracking',
    title: 'Scope 3 Emissions: The AI Approach to Supply Chain Carbon',
    category: 'Sustainability',
    summary: 'How AI-powered analytics can unlock visibility into the hardest-to-measure emissions category.',
    content: 'Full article content here...',
    publishDate: '2024-12-05',
    author: 'Marcus Osei',
    heroImage: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=800',
    readTime: '10 min read'
  }
];

export const teamMembers = [
  {
    id: '1',
    name: 'Dr. Lena van Berg',
    role: 'Co-Founder & Chief AI Architect',
    expertise: ['Machine Learning', 'LLM Systems', 'Agent Architecture', 'Climate Tech'],
    bio: 'Former AI research lead at a Fortune 500 tech company. PhD in Computer Science from TU Delft with focus on autonomous systems. 10+ years building production AI.',
    photo: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400',
    linkedin: 'https://linkedin.com'
  },
  {
    id: '2',
    name: 'Marcus Osei',
    role: 'Co-Founder & Head of Climate Strategy',
    expertise: ['Climate Policy', 'HFC Phase-Down', 'Carbon Markets', 'EU Regulation'],
    bio: 'Previously climate policy advisor to two European governments. Led emissions reduction programs achieving 40%+ CO₂ cuts. MSc in Environmental Policy from Oxford.',
    photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400',
    linkedin: 'https://linkedin.com'
  },
  {
    id: '3',
    name: 'Yuki Tanaka',
    role: 'Senior AI Engineer',
    expertise: ['Python', 'FastAPI', 'LLM Integration', 'Data Engineering'],
    bio: 'Full-stack AI engineer with expertise in building scalable agent systems. Previously at leading AI startup. Contributor to open-source ML projects.',
    photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400',
    linkedin: 'https://linkedin.com'
  },
  {
    id: '4',
    name: 'Sofia Ramirez',
    role: 'Sustainability Data Analyst',
    expertise: ['Emissions Modeling', 'GHG Protocol', 'Data Visualization', 'Scenario Analysis'],
    bio: 'Climate data specialist with background in industrial engineering. Expert in emissions accounting and compliance reporting. Certified GHG auditor.',
    photo: 'https://images.unsplash.com/photo-1598550874175-4d0ef436c909?w=400',
    linkedin: 'https://linkedin.com'
  }
];

export const faqs = [
  {
    question: 'What makes Futura different from other AI consultancies?',
    answer: 'We uniquely combine deep AI/ML engineering expertise with specialized climate policy knowledge. Our team has both built production AI systems at scale AND advised governments on emissions reduction strategies. This dual expertise means we deliver solutions that are both technically robust and aligned with real-world climate targets.'
  },
  {
    question: 'Do you work with SMEs or only large enterprises?',
    answer: 'We work with organizations of all sizes. For SMEs, we offer focused AI tools that solve specific problems (e.g., automated reporting, data extraction). For enterprises and governments, we deliver comprehensive multi-agent systems and strategic climate roadmaps. Our approach scales to your needs and budget.'
  },
  {
    question: 'What AI technologies do you use?',
    answer: 'We are technology-agnostic and choose the best tool for each use case. This includes large language models (GPT-5, Claude Sonnet, Gemini), classical ML, process automation, and custom-trained models. We prioritize production-grade solutions with robust security, monitoring, and compliance features.'
  },
  {
    question: 'How does AI help with HFC and CO₂ reduction?',
    answer: 'AI enables faster, more accurate scenario modeling, emissions forecasting, and policy impact analysis. Instead of manual spreadsheets taking weeks, our AI tools can evaluate hundreds of pathways in hours. This helps organizations make better decisions faster and track progress in real-time against climate targets.'
  },
  {
    question: 'What is the typical project timeline?',
    answer: 'Discovery workshops take 1-2 weeks. Prototypes and pilots typically run 6-12 weeks. Full implementations range from 3-9 months depending on scope. We work in agile sprints with regular check-ins, so you see progress quickly and can adjust priorities as needed.'
  },
  {
    question: 'Do you provide ongoing support after implementation?',
    answer: 'Yes. We offer managed services, SLA-backed support, and continuous optimization. Many clients start with a pilot, then expand to additional use cases. We can also train your team to manage systems independently if you prefer.'
  }
];
